import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { AuthDialog } from "./AuthDialog";
import { EditDialog } from "./EditDialog";
import { PdfPage } from "./PdfPage";
import { downloadBlob } from "../lib/download";
import { loadPdfDocument, type PdfDocument } from "../lib/pdf";
import { pickNearestBlockId } from "../lib/scrollSync";
import {
  buildDocumentContext,
  createTranslationBatches,
  hasValidSession,
  isApiConfigured,
  translateBatch
} from "../lib/translation";
import type { LocalProject, TextBlock } from "../types";

interface ReaderProps {
  project: LocalProject;
  onChange: (project: LocalProject) => void;
  onBack: () => void;
}

function Icon({
  name
}: {
  name:
    | "back"
    | "minus"
    | "plus"
    | "sync"
    | "translate"
    | "download"
    | "fullscreen"
    | "stop";
}) {
  const paths: Record<string, React.ReactNode> = {
    back: <path d="M15 18l-6-6 6-6" />,
    minus: <path d="M5 12h14" />,
    plus: <path d="M12 5v14M5 12h14" />,
    sync: (
      <>
        <path d="M20 7h-7a4 4 0 0 0-4 4v1" />
        <path d="M16 3l4 4-4 4M4 17h7a4 4 0 0 0 4-4v-1" />
        <path d="M8 21l-4-4 4-4" />
      </>
    ),
    translate: (
      <>
        <path d="M4 5h8M8 3v2m-3 4c1.4 2.7 3.5 4.7 6.5 6" />
        <path d="M11 8c-1.2 3.4-3.7 6.1-7 8M14 21l4-10 4 10m-6.7-3h5.4" />
      </>
    ),
    download: (
      <>
        <path d="M12 3v12m0 0l-4-4m4 4l4-4" />
        <path d="M5 19h14" />
      </>
    ),
    fullscreen: (
      <>
        <path d="M8 3H3v5M16 3h5v5M8 21H3v-5M16 21h5v-5" />
      </>
    ),
    stop: <rect x="6" y="6" width="12" height="12" rx="1" />
  };
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      {paths[name]}
    </svg>
  );
}

export function Reader({ project, onChange, onBack }: ReaderProps) {
  const projectRef = useRef(project);
  const sourceScroll = useRef<HTMLElement>(null);
  const translationScroll = useRef<HTMLElement>(null);
  const readerShell = useRef<HTMLDivElement>(null);
  const syncLock = useRef(false);
  const syncFrame = useRef<number | null>(null);
  const abortTranslation = useRef<AbortController | null>(null);
  const [pdfDocument, setPdfDocument] = useState<PdfDocument | null>(null);
  const [loadError, setLoadError] = useState("");
  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  const [editingBlock, setEditingBlock] = useState<TextBlock | null>(null);
  const [showAuth, setShowAuth] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [translating, setTranslating] = useState(false);
  const [translationTotal, setTranslationTotal] = useState(0);
  const [translationFinished, setTranslationFinished] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [pseudoFullscreen, setPseudoFullscreen] = useState(false);

  useEffect(() => {
    projectRef.current = project;
  }, [project]);

  const commit = useCallback(
    (update: (current: LocalProject) => LocalProject) => {
      const next = update(projectRef.current);
      projectRef.current = next;
      onChange(next);
    },
    [onChange]
  );

  useEffect(() => {
    let cancelled = false;
    void project.pdf
      .arrayBuffer()
      .then((bytes) => loadPdfDocument(bytes))
      .then((loaded) => {
        if (!cancelled) setPdfDocument(loaded);
      })
      .catch((reason) => {
        if (!cancelled) {
          setLoadError(
            reason instanceof Error ? reason.message : "无法打开 PDF。"
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [project.id, project.pdf]);

  useEffect(() => {
    if (!pdfDocument || !sourceScroll.current || project.settings.currentPage <= 1) {
      return;
    }
    const frame = requestAnimationFrame(() => {
      const selector = `[data-page="${project.settings.currentPage}"]`;
      sourceScroll.current
        ?.querySelector<HTMLElement>(selector)
        ?.scrollIntoView({ block: "start" });
      translationScroll.current
        ?.querySelector<HTMLElement>(selector)
        ?.scrollIntoView({ block: "start" });
    });
    return () => cancelAnimationFrame(frame);
  }, [pdfDocument, project.id]);

  const allBlocks = useMemo(
    () => project.layout.flatMap((page) => page.blocks),
    [project.layout]
  );
  const translatableCount = allBlocks.filter(
    (block) => block.translatable
  ).length;
  const translatedCount = allBlocks.filter((block) => {
    const translation = project.translations[block.id];
    return translation?.status === "done" || translation?.status === "edited";
  }).length;
  const percent = translatableCount
    ? Math.round((translatedCount / translatableCount) * 100)
    : 0;

  const updateCurrentPage = (container: HTMLElement) => {
    const containerTop = container.getBoundingClientRect().top;
    let nearest = projectRef.current.settings.currentPage;
    let distance = Number.POSITIVE_INFINITY;
    container.querySelectorAll<HTMLElement>("[data-page]").forEach((element) => {
      const currentDistance = Math.abs(
        element.getBoundingClientRect().top - containerTop - 12
      );
      if (currentDistance < distance) {
        distance = currentDistance;
        nearest = Number(element.dataset.page);
      }
    });
    if (nearest !== projectRef.current.settings.currentPage) {
      commit((current) => ({
        ...current,
        updatedAt: Date.now(),
        settings: { ...current.settings, currentPage: nearest }
      }));
    }
  };

  const handleScroll = (
    source: "source" | "translation",
    event: React.UIEvent<HTMLElement>
  ) => {
    const current = event.currentTarget;
    updateCurrentPage(current);
    if (!projectRef.current.settings.syncScroll || syncLock.current) return;
    const other =
      source === "source" ? translationScroll.current : sourceScroll.current;
    if (!other) return;
    if (syncFrame.current !== null) cancelAnimationFrame(syncFrame.current);
    syncFrame.current = requestAnimationFrame(() => {
      const currentRect = current.getBoundingClientRect();
      const anchor = Math.min(96, current.clientHeight * 0.2);
      const positions = Array.from(
        current.querySelectorAll<HTMLElement>("[data-block-id]")
      ).map((element) => {
        const rect = element.getBoundingClientRect();
        return {
          id: element.dataset.blockId ?? "",
          top: rect.top - currentRect.top,
          bottom: rect.bottom - currentRect.top
        };
      });
      const blockId = pickNearestBlockId(positions, anchor);
      const target = blockId
        ? Array.from(
            other.querySelectorAll<HTMLElement>("[data-block-id]")
          ).find((element) => element.dataset.blockId === blockId)
        : null;

      syncLock.current = true;
      if (target) {
        const otherRect = other.getBoundingClientRect();
        other.scrollTop += target.getBoundingClientRect().top - otherRect.top - anchor;
      } else {
        const pageNumber = projectRef.current.settings.currentPage;
        const page = other.querySelector<HTMLElement>(
          `[data-page="${pageNumber}"]`
        );
        if (page) {
          const otherRect = other.getBoundingClientRect();
          other.scrollTop += page.getBoundingClientRect().top - otherRect.top - 12;
        }
      }
      requestAnimationFrame(() => {
        syncLock.current = false;
      });
      syncFrame.current = null;
    });
  };

  const runTranslation = useCallback(async () => {
    const currentProject = projectRef.current;
    const pending = currentProject.layout
      .flatMap((page) => page.blocks)
      .filter((block) => {
        if (!block.translatable) return false;
        const translation = currentProject.translations[block.id];
        return (
          !translation ||
          translation.status === "pending" ||
          translation.status === "failed"
        );
      });

    if (!pending.length) {
      setNotice("所有可翻译文本块都已完成。");
      return;
    }

    const batches = createTranslationBatches(pending);
    const controller = new AbortController();
    abortTranslation.current = controller;
    setTranslating(true);
    setTranslationTotal(pending.length);
    setTranslationFinished(0);
    setError("");
    setNotice("");
    const context = buildDocumentContext(currentProject);
    let nextBatch = 0;
    let completed = 0;
    const errors: string[] = [];

    const worker = async () => {
      while (!controller.signal.aborted) {
        const index = nextBatch;
        nextBatch += 1;
        if (index >= batches.length) return;
        const batch = batches[index];
        commit((current) => {
          const translations = { ...current.translations };
          batch.forEach((block) => {
            translations[block.id] = {
              id: block.id,
              text: translations[block.id]?.text ?? "",
              status: "translating",
              updatedAt: Date.now()
            };
          });
          return { ...current, translations, updatedAt: Date.now() };
        });

        try {
          const result = await translateBatch(
            currentProject.id,
            context,
            batch,
            controller.signal
          );
          commit((current) => {
            const translations = { ...current.translations };
            result.forEach((item) => {
              translations[item.id] = {
                id: item.id,
                text: item.text,
                status: "done",
                updatedAt: Date.now()
              };
            });
            return { ...current, translations, updatedAt: Date.now() };
          });
          completed += batch.length;
          setTranslationFinished(completed);
        } catch (reason) {
          if (reason instanceof DOMException && reason.name === "AbortError") {
            return;
          }
          const caught =
            reason instanceof Error ? reason : new Error("翻译请求失败。");
          if (!errors.length) errors.push(caught.message);
          commit((current) => {
            const translations = { ...current.translations };
            batch.forEach((block) => {
              translations[block.id] = {
                id: block.id,
                text: translations[block.id]?.text ?? "",
                status: "failed",
                updatedAt: Date.now(),
                error: caught.message
              };
            });
            return { ...current, translations, updatedAt: Date.now() };
          });
        }
      }
    };

    await Promise.all([worker(), worker()]);
    setTranslating(false);
    abortTranslation.current = null;
    if (controller.signal.aborted) {
      commit((current) => {
        const translations = { ...current.translations };
        Object.values(translations).forEach((item) => {
          if (item.status === "translating") item.status = "pending";
        });
        return { ...current, translations, updatedAt: Date.now() };
      });
      setNotice("翻译已暂停，进度已经保存。");
    } else if (errors.length) {
      setError(`部分文本块翻译失败：${errors[0]}`);
    } else {
      setNotice("论文翻译完成，结果已自动保存在本机。");
    }
  }, [commit]);

  const requestTranslation = () => {
    if (!isApiConfigured()) {
      setError(
        "尚未配置翻译服务地址。请在 .env.local 中设置 VITE_API_BASE_URL。"
      );
      return;
    }
    if (!hasValidSession()) {
      setShowAuth(true);
      return;
    }
    void runTranslation();
  };

  const exportPdf = async () => {
    if (!translatedCount) {
      setError("请先完成至少一部分翻译，再导出译文 PDF。");
      return;
    }
    setExporting(true);
    setError("");
    try {
      const { createTranslatedPdf, PdfOverflowError } = await import(
        "../lib/exportPdf"
      );
      const blob = await createTranslatedPdf(projectRef.current);
      downloadBlob(
        blob,
        `${project.name.replace(/\.pdf$/i, "")}-中文译文.pdf`
      );
      setNotice("中文译文 PDF 已生成。");
    } catch (reason) {
      if (
        reason instanceof Error &&
        reason.name === "PdfOverflowError" &&
        "blockIds" in reason &&
        Array.isArray(reason.blockIds)
      ) {
        setHighlightedId(reason.blockIds[0] ?? null);
        setError(
          `有 ${reason.blockIds.length} 段译文过长，暂未导出。请点击右侧对应段落精简后重试。`
        );
      } else {
        setError(reason instanceof Error ? reason.message : "PDF 导出失败。");
      }
    } finally {
      setExporting(false);
    }
  };

  const toggleFullscreen = async () => {
    if (globalThis.document.fullscreenElement) {
      await globalThis.document.exitFullscreen();
      return;
    }
    try {
      await readerShell.current?.requestFullscreen();
    } catch {
      setPseudoFullscreen((value) => !value);
    }
  };

  if (loadError) {
    return (
      <main className="reader-error">
        <h1>无法打开论文</h1>
        <p>{loadError}</p>
        <button className="primary-button" onClick={onBack}>
          返回项目列表
        </button>
      </main>
    );
  }

  return (
    <div
      ref={readerShell}
      className={`reader-shell ${
        error || notice ? "has-notice" : ""
      } ${pseudoFullscreen ? "pseudo-fullscreen" : ""}`}
    >
      <header className="reader-toolbar">
        <div className="toolbar-leading">
          <button className="icon-button" onClick={onBack} title="返回项目列表">
            <Icon name="back" />
          </button>
          <div className="reader-title">
            <strong>{project.name}</strong>
            <span>
              第 {project.settings.currentPage} / {project.pageCount} 页 · 本地已保存
            </span>
          </div>
        </div>

        <div className="toolbar-center">
          <button
            className="icon-button compact"
            onClick={() =>
              commit((current) => ({
                ...current,
                settings: {
                  ...current.settings,
                  zoom: Math.max(0.55, current.settings.zoom - 0.1)
                }
              }))
            }
            title="缩小"
          >
            <Icon name="minus" />
          </button>
          <span className="zoom-label">
            {Math.round(project.settings.zoom * 100)}%
          </span>
          <button
            className="icon-button compact"
            onClick={() =>
              commit((current) => ({
                ...current,
                settings: {
                  ...current.settings,
                  zoom: Math.min(1.8, current.settings.zoom + 0.1)
                }
              }))
            }
            title="放大"
          >
            <Icon name="plus" />
          </button>
          <span className="toolbar-separator" />
          <button
            className={`toolbar-toggle ${project.settings.syncScroll ? "active" : ""}`}
            onClick={() =>
              commit((current) => ({
                ...current,
                settings: {
                  ...current.settings,
                  syncScroll: !current.settings.syncScroll
                }
              }))
            }
            title="同步滚动"
          >
            <Icon name="sync" />
            同步
          </button>
        </div>

        <div className="toolbar-actions">
          {translating ? (
            <button
              className="secondary-button toolbar-button"
              onClick={() => abortTranslation.current?.abort()}
            >
              <Icon name="stop" />
              暂停 {translationFinished}/{translationTotal}
            </button>
          ) : (
            <button
              className="primary-button toolbar-button"
              onClick={requestTranslation}
            >
              <Icon name="translate" />
              {percent ? `继续翻译 ${percent}%` : "开始翻译"}
            </button>
          )}
          <button
            className="secondary-button toolbar-button"
            onClick={() => void exportPdf()}
            disabled={exporting}
          >
            <Icon name="download" />
            {exporting ? "正在生成…" : "导出 PDF"}
          </button>
          <button
            className="icon-button"
            onClick={() => void toggleFullscreen()}
            title="全屏阅读"
          >
            <Icon name="fullscreen" />
          </button>
        </div>
      </header>

      {(error || notice) && (
        <div className={`reader-notice ${error ? "is-error" : ""}`}>
          <span>{error || notice}</span>
          <button
            onClick={() => {
              setError("");
              setNotice("");
            }}
          >
            ×
          </button>
        </div>
      )}

      <div className="reader-columns-heading">
        <div>
          <span className="language-badge">EN</span>
          英文原文
        </div>
        <div>
          <span className="language-badge zh">中</span>
          中文译文
          <small>{percent}%</small>
        </div>
      </div>

      <main className="reader-workspace">
        <section
          ref={sourceScroll}
          className="document-pane source-pane"
          onScroll={(event) => handleScroll("source", event)}
          aria-label="英文原文"
        >
          <div className="pages-stack">
            {pdfDocument ? (
              project.layout.map((page) => (
                <PdfPage
                  key={`source-${page.pageNumber}`}
                  document={pdfDocument}
                  page={page}
                  zoom={project.settings.zoom}
                  side="source"
                  translations={project.translations}
                  highlightedId={highlightedId}
                  onHighlight={setHighlightedId}
                  onEdit={setEditingBlock}
                />
              ))
            ) : (
              <div className="document-loading">
                <span className="spinner" />
                正在打开论文…
              </div>
            )}
          </div>
        </section>

        <div className="reader-divider" />

        <section
          ref={translationScroll}
          className="document-pane translation-pane"
          onScroll={(event) => handleScroll("translation", event)}
          aria-label="中文译文"
        >
          <div className="pages-stack">
            {pdfDocument ? (
              project.layout.map((page) => (
                <PdfPage
                  key={`translation-${page.pageNumber}`}
                  document={pdfDocument}
                  page={page}
                  zoom={project.settings.zoom}
                  side="translation"
                  translations={project.translations}
                  highlightedId={highlightedId}
                  onHighlight={setHighlightedId}
                  onEdit={setEditingBlock}
                />
              ))
            ) : (
              <div className="document-loading">
                <span className="spinner" />
                正在打开论文…
              </div>
            )}
          </div>
        </section>
      </main>

      {showAuth && (
        <AuthDialog
          onClose={() => setShowAuth(false)}
          onAuthenticated={() => {
            setShowAuth(false);
            void runTranslation();
          }}
        />
      )}
      {editingBlock && (
        <EditDialog
          source={editingBlock.text}
          translation={project.translations[editingBlock.id]?.text ?? ""}
          onCancel={() => setEditingBlock(null)}
          onSave={(text) => {
            commit((current) => ({
              ...current,
              updatedAt: Date.now(),
              translations: {
                ...current.translations,
                [editingBlock.id]: {
                  id: editingBlock.id,
                  text,
                  status: "edited",
                  updatedAt: Date.now()
                }
              }
            }));
            setEditingBlock(null);
            setNotice("译文修改已自动保存。");
          }}
        />
      )}
    </div>
  );
}
